import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorMessage from '../../components/ui/ErrorMessage';
import SuccessMessage from '../../components/ui/SuccessMessage';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import MCQFormModal from '../../components/admin/MCQFormModal';
import mcqService from '../../services/mcqService';
import courseService from '../../services/courseService';

/**
 * ManageMCQs - Admin page for managing MCQs for a specific course
 * Displays all MCQs with options to create, edit, delete, and manage questions
 */
const ManageMCQs = () => {
  const { courseId } = useParams();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [mcqs, setMcqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMCQ, setEditingMCQ] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, mcqId: null, mcqTitle: '' });

  // Redirect if not admin
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (user && user.role !== 'admin') {
      navigate('/');
      return;
    }
  }, [isAuthenticated, user, navigate]);

  // Fetch course and MCQs on mount
  useEffect(() => {
    if (user && user.role === 'admin' && courseId) {
      fetchCourseAndMCQs();
    }
  }, [user, courseId]);

  /**
   * Fetch course details and MCQs
   */
  const fetchCourseAndMCQs = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch course details
      const courseData = await courseService.getCourseById(courseId);
      setCourse(courseData);

      // Fetch MCQs for this course
      const mcqData = await mcqService.getAllMCQs(courseId);
      setMcqs(mcqData);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError(err.response?.data?.detail || 'Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Open modal for creating a new MCQ
   */
  const handleCreateMCQ = () => {
    setEditingMCQ(null);
    setIsModalOpen(true);
  };

  /**
   * Open modal for editing an existing MCQ
   */
  const handleEditMCQ = (mcq) => {
    setEditingMCQ(mcq);
    setIsModalOpen(true);
  };

  /**
   * Navigate to question management page
   */
  const handleManageQuestions = (mcqId) => {
    navigate(`/admin/mcqs/${mcqId}/questions`);
  };

  /**
   * Open delete confirmation dialog
   */
  const handleDeleteClick = (mcq) => {
    setDeleteDialog({
      isOpen: true,
      mcqId: mcq.id,
      mcqTitle: mcq.title
    });
  };

  /**
   * Delete an MCQ after confirmation
   */
  const handleDeleteConfirm = async () => {
    try {
      await mcqService.deleteMCQ(deleteDialog.mcqId);
      setSuccessMessage(`MCQ "${deleteDialog.mcqTitle}" deleted successfully!`);
      setDeleteDialog({ isOpen: false, mcqId: null, mcqTitle: '' });
      // Refresh MCQs list
      fetchCourseAndMCQs();
    } catch (err) {
      console.error('Failed to delete MCQ:', err);
      setError(err.response?.data?.detail || 'Failed to delete MCQ. Please try again.');
    }
  };

  /**
   * Handle successful MCQ save (create or update)
   */
  const handleMCQSaved = () => {
    setIsModalOpen(false);
    setEditingMCQ(null);
    setSuccessMessage(editingMCQ ? 'MCQ updated successfully!' : 'MCQ created successfully!');
    // Refresh MCQs list
    fetchCourseAndMCQs();
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-grow">
          <LoadingSpinner message="Loading MCQs..." />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <main className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <button
                onClick={() => navigate('/admin/courses')}
                className="text-blue-600 hover:text-blue-800 mb-2 flex items-center gap-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Courses
              </button>
              <h1 className="text-3xl font-bold text-gray-900">
                MCQ Management: {course?.title}
              </h1>
              <p className="text-gray-600 mt-1">Create and manage MCQ sets for this course</p>
            </div>
            <button
              onClick={handleCreateMCQ}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl font-semibold"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create MCQ
            </button>
          </div>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <SuccessMessage message={successMessage} onClose={() => setSuccessMessage('')} />
        )}
        {error && (
          <ErrorMessage message={error} onRetry={fetchCourseAndMCQs} />
        )}

        {/* MCQs List */}
        {mcqs.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg shadow">
            <svg className="w-24 h-24 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No MCQs Yet</h3>
            <p className="text-gray-500 mb-6">Get started by creating your first MCQ set</p>
            <button
              onClick={handleCreateMCQ}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create First MCQ
            </button>
          </div>
        ) : (
          <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {mcqs.map((mcq) => (
              <div key={mcq.id} className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow p-6 border border-gray-200">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-grow">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{mcq.title}</h3>
                    {mcq.description && (
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">{mcq.description}</p>
                    )}
                  </div>
                  {mcq.is_preview && (
                    <span className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full ml-2">
                      Preview
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className={mcq.question_count === 0 ? 'text-red-600 font-semibold' : ''}>
                      {mcq.question_count} Questions
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    <span>Order: {mcq.order}</span>
                  </div>
                </div>

                {/* Warning for MCQs with no questions */}
                {mcq.question_count === 0 && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                    <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <p className="text-xs text-yellow-800">
                      <span className="font-semibold">Not visible to students</span> - Add questions to make this MCQ available
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleManageQuestions(mcq.id)}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all font-medium text-sm"
                  >
                    Manage Questions
                  </button>
                  <button
                    onClick={() => handleEditMCQ(mcq)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    title="Edit MCQ"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDeleteClick(mcq)}
                    className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                    title="Delete MCQ"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Footer />

      {/* MCQ Form Modal */}
      <MCQFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingMCQ(null);
        }}
        onSave={handleMCQSaved}
        mcq={editingMCQ}
        courseId={courseId}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        title="Delete MCQ"
        message={`Are you sure you want to delete "${deleteDialog.mcqTitle}"? This will also delete all questions in this MCQ. This action cannot be undone.`}
        confirmText="Delete"
        confirmStyle="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog({ isOpen: false, mcqId: null, mcqTitle: '' })}
      />
    </div>
  );
};

export default ManageMCQs;
